import { isEmpty } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { Box, LoaderOverlay, Stack, Typography } from '@common-components';
import { ExtractionType } from 'enums';
import { useBoolean, useCompareQuotes, useMount, useMutateQuote } from 'hooks';
import { messages } from 'i18n';
import { AIButtonWithPopup } from 'broker/pages/SubmissionWorkspacePage/components/AIGeneratedContent/AIButtonWithPopup';
import { useTrackFileExtractions } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/Quote/components/useTrackFileExtractions';
import { ComposeProps } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/QuoteComparisonInsights/components/types';
import { insightsFormFieldsConfig } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/QuoteComparisonInsights/form-methods/config/insightsFieldsConfig';
import { InsightsFieldsNames } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/QuoteComparisonInsights/form-methods/enums';
import { useAIContext } from 'broker/pages/SubmissionWorkspacePage/contexts/ai/AIContextProvider';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import ComparisonInsightsEditor from './ComparisonInsightsEditor';
import { QuotesList } from './QuotesList';

export default function Compose({ quotes, setInsightPopulatedByAI, expiringPolicyFileId }: ComposeProps) {
  const {
    title,
    loading,
    extractingData,
    quoteComparisonInsights: { popupTitle, generatingQuoteComparisonInsights },
  } = messages.quoteComparisonPage.compose;

  const [isLoadingMissingExtractionsMetadata, { off: missingExtractionsMetadataLoaded }] = useBoolean(true);
  const { getMissingExtractions } = useCompareQuotes();
  const { partialSubmission } = useSubmissionsWorkspace();
  const { getValues } = useFormContext();
  const { extractQuoteData } = useMutateQuote();
  const requestMissingExtraction = async (fileId: string, extractionType: ExtractionType) => {
    await extractQuoteData({ fileId, submissionId: partialSubmission!.id, extractionType });
  };
  const { startTrackingFileExtraction, hasPendingExtractions } = useTrackFileExtractions();

  useMount(() => {
    const handleMissingDataExtractions = async () => {
      const result = await getMissingExtractions({
        quoteIds: quotes.map((quote) => quote.id),
        expiringPolicyFileId,
        submissionId: partialSubmission!.id,
      });

      if (!result) {
        missingExtractionsMetadataLoaded();
        return;
      }

      result.missingExtractions.forEach((extraction) => {
        startTrackingFileExtraction(extraction.boxFileId);
      });

      await Promise.all(
        result.missingExtractions.map((extraction) =>
          requestMissingExtraction(extraction.boxFileId, extraction.extractionType),
        ),
      );

      missingExtractionsMetadataLoaded();
    };

    handleMissingDataExtractions();
  });

  const isEmptyQuoteInsights = isEmpty(getValues(insightsFormFieldsConfig[InsightsFieldsNames.Insights].name));

  const { isLoading: isQuoteInsightsLoading, isContentStreaming: isQuoteInsightsStreaming } = useAIContext();
  const isExtractingData = isLoadingMissingExtractionsMetadata || hasPendingExtractions;
  const isComparisonInsightsLoading = isEmptyQuoteInsights && (isQuoteInsightsLoading || isQuoteInsightsStreaming);
  const showLoaderOverlay = isExtractingData || isComparisonInsightsLoading;

  return (
    <Stack pt={4} px={6} height={1}>
      <QuotesList quotes={quotes} expiringPolicyFileId={expiringPolicyFileId} />
      <Stack mt={3} height={1}>
        <Typography variant="body2Bold" component="div" mb={1}>
          {title}
        </Typography>
        <Box position="relative" height={1}>
          {/* Prevent rendering ComparisonInsightsEditor before data extraction is ready since the AI thread (for comparison) starts as it mounts */}
          {!isExtractingData && (
            <ComparisonInsightsEditor
              quotes={quotes}
              setInsightPopulatedByAI={setInsightPopulatedByAI}
              expiringPolicyFileId={expiringPolicyFileId}
              isDisabled={isComparisonInsightsLoading}
            />
          )}
          {showLoaderOverlay && <LoaderOverlay text={isExtractingData ? extractingData : loading} />}
        </Box>
      </Stack>
      <AIButtonWithPopup popupTitle={popupTitle} tooltipText={generatingQuoteComparisonInsights} />
    </Stack>
  );
}
